import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import lottoApi from "../api";
import { useAuth } from "../../contexts/JWTContext";
import { useAsync, pushToast } from "../lib";
import { Empty, Sheet, Skeleton, LoginGate } from "../ui";

function makeImageUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = lottoApi.base || "";
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
}

export default function PromotionPage() {
  const { user, isAuthenticated } = useAuth();
  const nav = useNavigate();
  const uid = user?.id;

  const {
    data: detail,
    reload: reloadDetail,
  } = useAsync(() => (uid ? lottoApi.userDetail(uid) : Promise.resolve(null)), [uid]);
  const {
    data: available,
    loading,
    reload: reloadAvailable,
  } = useAsync(() => (uid ? lottoApi.availablePromotions(uid) : Promise.resolve([])), [uid]);

  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const promoCards = useMemo(
    () =>
      (available || []).map((p) => ({
        id: p?.PromotionID,
        detail: p?.Detail,
        image: makeImageUrl(p?.imageUrl),
      })),
    [available],
  );

  const selectedDetail = detail?.PromotionDetail || "";
  const locked = detail?.PromotionStatus === "ACTIVE" || !!detail?.withdrawConditionType;

  useEffect(() => {
    if (uid) {
      reloadDetail();
      reloadAvailable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  if (!isAuthenticated)
    return (
      <LoginGate
        icon="fluent-emoji-flat:wrapped-gift"
        title="เข้าสู่ระบบก่อนเลือกโปรโมชั่น"
        desc="เลือกโปรโมชั่นที่เหมาะกับคุณแล้วรับโบนัส"
        perks={["ดูโปรโมชั่นทั้งหมดที่เปิดอยู่","อ่านเงื่อนไขชัดเจนก่อนรับ","รับโบนัสเข้ากระเป๋าทันที"]}
        onLogin={() => nav("/auth")}
      />
    );

  const apply = async (promotionId, promoDetail) => {
    setBusy(true);
    try {
      await lottoApi.selectPromotion(uid, promotionId, promoDetail);
      pushToast(
        promotionId && promotionId !== "NULL" ? "เลือกโปรโมชั่นเรียบร้อย" : "เปลี่ยนเป็นไม่รับโปรโมชั่นแล้ว",
        "ok",
      );
      reloadDetail();
      reloadAvailable();
    } catch {
      pushToast("ไม่สามารถอัปเดตโปรโมชั่นได้", "err");
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  return (
    <div style={{ marginTop: 18 }}>
      <div
        className="lx-card vx-pagehead"
        style={{
          textAlign: "center",
      }}
      >
        <Icon icon="solar:gift-bold-duotone" width={40} style={{ color: "var(--lx-accent-strong)" }} />
        <div className="lx-h1" style={{ marginTop: 8 }}>
          จัดการโปรโมชั่น
        </div>
        <p className="lx-muted" style={{ margin: "6px 0 0" }}>
          เลือกโปรโมชั่นที่ต้องการรับอัตโนมัติเมื่อฝากเงิน
        </p>
      </div>

      <div className="lx-card" style={{ marginTop: 14 }}>
        <div className="lx-spread">
          <div className="lx-row" style={{ gap: 10 }}>
            <Icon
              icon={selectedDetail ? "solar:verified-check-bold" : "solar:close-circle-bold"}
              width={22}
              style={{ color: selectedDetail ? "var(--lx-accent2-strong)" : "var(--lx-text-mute)" }}
            />
            <div>
              <div className="lx-muted" style={{ fontSize: 12 }}>
                โปรโมชั่นที่เลือกไว้
              </div>
              <div style={{ fontWeight: 800 }}>{selectedDetail || "ไม่ได้รับโปรโมชั่น"}</div>
            </div>
          </div>
          {!locked && selectedDetail && (
            <button className="lx-btn ghost sm" disabled={busy} onClick={() => apply("NULL", "NULL")}>
              ไม่รับโปร
            </button>
          )}
        </div>
      </div>

      {locked && (
        <div className="lx-card" style={{ marginTop: 12, borderColor: "color-mix(in srgb, var(--lx-red) 40%, transparent)" }}>
          <div className="lx-row" style={{ gap: 10 }}>
            <Icon icon="solar:danger-triangle-bold" width={20} style={{ color: "var(--lx-red-strong)" }} />
            <div>
              <b style={{ color: "var(--lx-red-strong)" }}>ไม่สามารถเปลี่ยนโปรโมชั่นได้</b>
              <p className="lx-muted" style={{ fontSize: 12, margin: "2px 0 0" }}>
                ท่านอยู่ระหว่างใช้โปรโมชั่น หรือมียอดที่ต้องเล่น กรุณาเคลียร์ยอดเล่นให้ครบก่อน
              </p>
            </div>
          </div>
        </div>
      )}

      <section className="lx-section">
        <h2 className="lx-h2" style={{ marginBottom: 10 }}>
          โปรโมชั่นที่เลือกได้
        </h2>
        {loading && (
          <div className="lx-grid">
            {[1, 2].map((i) => (
              <Skeleton key={i} h={140} />
            ))}
          </div>
        )}
        {!loading && !promoCards.length && (
          <div className="lx-card">
            <Empty icon="solar:gift-bold-duotone">ไม่มีโปรโมชั่นให้เลือกในขณะนี้</Empty>
          </div>
        )}
        {!loading && !!promoCards.length && (
          <div className="lx-grid">
            {promoCards.map((it) => {
              const active = selectedDetail === it.detail;
              return (
                <div
                  key={it.id}
                  className="lx-lotto"
                  style={{
                    opacity: locked && !active ? 0.6 : 1,
                    cursor: locked ? "not-allowed" : "pointer",
                    borderColor: active ? "color-mix(in srgb, var(--lx-accent-2) 50%, transparent)" : undefined,
                  }}
                  onClick={() => !locked && setPending(it)}
                >
                  {it.image ? (
                    <img
                      src={it.image}
                      alt={it.detail}
                      style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 10, marginBottom: 8 }}
                    />
                  ) : (
                    <div
                      className="lx-flagbadge"
                      style={{ width: "100%", height: 90, borderRadius: 10, marginBottom: 8 }}
                    >
                      <Icon icon="solar:gallery-bold-duotone" width={30} />
                    </div>
                  )}
                  <div className="name" style={{ fontSize: 14 }}>
                    {it.detail}
                  </div>
                  <div className="foot">
                    <span>{active ? "✓ กำลังใช้งาน" : locked ? "ไม่สามารถเลือกได้" : "แตะเพื่อเลือก"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Sheet
        open={!!pending}
        onClose={() => !busy && setPending(null)}
        title="ยืนยันการเลือกโปรโมชั่น"
        footer={
          <div className="lx-row" style={{ gap: 10 }}>
            <button className="lx-btn ghost block" disabled={busy} onClick={() => setPending(null)}>
              ยกเลิก
            </button>
            <button
              className="lx-btn gradient block"
              disabled={busy}
              onClick={() => pending && apply(pending.id, pending.detail)}
            >
              {busy ? "..." : "ยืนยันเลือก"}
            </button>
          </div>
        }
      >
        {pending?.image && (
          <img
            src={pending.image}
            alt={pending.detail}
            style={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 12, marginBottom: 12 }}
          />
        )}
        <p style={{ margin: 0 }}>ท่านต้องการเลือกโปรโมชั่นนี้หรือไม่?</p>
        <b>{pending?.detail}</b>
      </Sheet>
    </div>
  );
}
